/// <amd-module name="Work/Views/MyWork/DataProviders/MyWorkProvider" />
import { IVssPageContext } from "VSS/Platform/Context";

import * as ContributionRoutes from "../Constants/ContributionRoutes";
import { AccountMyWorkResult, QueryOption, AccountWorkWorkItemModel, WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { IMyWorkProvider, FormattedDate } from "DataProviders/IMyWorkProvider";
import { MyWorkProviderBase, activityDateFieldName } from "DataProviders/MyWorkProviderBase";
import { routeUrl } from "VSS/Platform/Location";
import { format } from "VSS/Core/Util/String";
import { ago } from "VSSUI/Utilities/Date";
import * as MyWorkResources from "Work/Views/MyWork/Resources";

/**
 * Data provider to retrieve data for the 'Assigned To Me' and 'Followed By Me' section.
 */
export class MyWorkProvider extends MyWorkProviderBase<AccountMyWorkResult> implements IMyWorkProvider {
    private queryOption: QueryOption;
    private displayItems: WorkItem[] = [];

    constructor(context: IVssPageContext, queryOption: QueryOption) {
        super(context);
        this.queryOption = queryOption;
    }

    public getLimitExceeded(): boolean {
        if (this.queryResult) {
            return this.queryResult.querySizeLimitExceeded;
        }

        return super.getLimitExceeded();
    }

    public getWorkItems(): WorkItem[] {
        return this.displayItems;
    }

    protected getData(): Promise<AccountMyWorkResult> {
        const client = this.context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        const promise = client.getAccountMyWorkData(this.queryOption);

        promise.then((results: AccountMyWorkResult) => {
            this.setDisplayWorkItems(results.workItemDetails);
        });

        return promise;
    }

    protected getDataLength(): number {
        if (this.queryResult) {
            return this.queryResult.workItemDetails.length;
        }

        return 0;
    }

    private setDisplayWorkItems(workItems: AccountWorkWorkItemModel[]): void {
        this.displayItems = workItems.map(item => {
            const projectName = item.teamProject;
            const id = item.id;
            const workItemEditUrl = projectName
                ? routeUrl(this.context, ContributionRoutes.WorkItemRoute, {
                      project: projectName,
                      id: `${id}`
                  })
                : "";
            const projectUrl = projectName ? routeUrl(this.context, ContributionRoutes.ProjectRoute, { project: projectName }) : "";
            const customChangedDate = format(MyWorkResources.LastUpdatedDateFormat, ago(item.changedDate));
            const formattedDate: FormattedDate = {
                date: item.changedDate,
                formattedDate: customChangedDate
            };

            return ({
                fields: {
                    [CoreFieldRefNames.AssignedTo]: item.assignedTo,
                    [CoreFieldRefNames.State]: item.state,
                    [CoreFieldRefNames.TeamProject]: item.teamProject,
                    [CoreFieldRefNames.Title]: item.title,
                    [CoreFieldRefNames.WorkItemType]: item.workItemType,
                    [activityDateFieldName]: formattedDate
                },
                id: id,
                _links: {
                    html: workItemEditUrl,
                    project: projectUrl
                }
            } as unknown) as WorkItem;
        });
    }
}
